import { createElement } from "react";

import { BaseHandler, GameConfig } from "@/lib/BaseHandler";
import { CardItem, PalworldItem } from "@/interfaces/CardItem";
import { isPalworldStructure } from "@/lib/games/palworld/getCardData";
import {
  getCardImage,
  getCardData,
  getAltArts,
  getAACardId,
  getAACardIdData,
  getAAFromCardId,
  cleanCardData,
} from "@/lib/games/palworld/getCardDataLocal";
import { getImgproxyUrl } from "@/lib/imgproxy";
import { comparePalworldCardNumbers } from "@/lib/games/palworld/sortCards";
import { CDN } from "@/lib/cdn";
import PalworldCardText from "@/components/CardPage/PalworldCardText";
import { palworldFilterSpec } from "./filters";

// Decklist sorting function for Palworld
function sortDecklist(cardList: string): string {
  const cards = cardList.split(";");

  // Parse and collapse duplicates (e.g., from alternate arts)
  const cardMap = new Map<
    string,
    { quantity: number; cardData?: PalworldItem }
  >();

  for (const entry of cards) {
    const [quantity, cardId] = entry.split("x");
    const qty = parseInt(quantity) || 0;
    const cardData = getCardData(cardId) as PalworldItem | undefined;

    if (cardMap.has(cardId)) {
      const existing = cardMap.get(cardId)!;
      existing.quantity += qty;
    } else {
      cardMap.set(cardId, { quantity: qty, cardData });
    }
  }

  // Convert map to array
  const cardEntries = Array.from(cardMap.entries()).map(
    ([cardId, { quantity, cardData }]) => ({
      quantity,
      cardId,
      cardData,
    }),
  );

  // Define type order
  const typeOrder: Record<string, number> = {
    Pal: 0,
    Gear: 1,
    Event: 2,
    Structure: 3,
  };

  // Sort the cards
  cardEntries.sort((a, b) => {
    // If card data is missing, push to end
    if (!a.cardData) return 1;
    if (!b.cardData) return -1;

    // 1. Sort by type (Pals, Gear, Events, Structures)
    const typeA = typeOrder[a.cardData.card_kind] ?? 999;
    const typeB = typeOrder[b.cardData.card_kind] ?? 999;
    if (typeA !== typeB) return typeA - typeB;

    // 2. Sort by cost (ascending)
    const costA = a.cardData.cost ?? 0;
    const costB = b.cardData.cost ?? 0;
    if (costA !== costB) return costA - costB;

    // 3. Type-specific sorting
    const cardKind = a.cardData.card_kind;

    if (cardKind === "Pal") {
      // Pals: cost → power → attack (strike) → name
      const powerA = a.cardData.power ?? 0;
      const powerB = b.cardData.power ?? 0;
      if (powerA !== powerB) return powerA - powerB;

      const attackA = a.cardData.attack ?? 0;
      const attackB = b.cardData.attack ?? 0;
      if (attackA !== attackB) return attackA - attackB;
    } else if (cardKind === "Structure") {
      // Structures: cost → HP (power) → name
      const hpA = a.cardData.power ?? 0;
      const hpB = b.cardData.power ?? 0;
      if (hpA !== hpB) return hpA - hpB;
    }
    // Gear and Events: cost → name (handled below)

    // 4. Finally, sort by name (ascending)
    const nameA = a.cardData.name || a.cardId;
    const nameB = b.cardData.name || b.cardId;
    return nameA.localeCompare(nameB);
  });

  // Reconstruct the card list string
  return cardEntries.map((e) => `${e.quantity}x${e.cardId}`).join(";");
}
import {
  filterCardsFromSpec,
  getDefaultFiltersFromSpec,
} from "@/components/DeckBuilder/filters/spec";
import { getArchetypeData } from "@/lib/games/palworld/archetypeManager";

const palworldConfig: GameConfig = {
  cdnBaseUrl: CDN,
  setImagePath: "/games/palworld/images/set-squares/",
  hasLeader: false,

  // Palworld uses archetypes instead of leaders
  getLeaderData: getArchetypeData,

  cardDataFunctions: {
    getCardData,
    getCardImage,
    getAltArts,
    getAACardId,
    getAACardIdData,
    getAAFromCardId,
    cleanCardData,
  },

  // The filter bar, the default state and the filtering all come off the one
  // declaration in ./filters.
  getDefaultFilters: () => getDefaultFiltersFromSpec(palworldFilterSpec),
  filterCards: (cards, filters, defaultFilters, deckCardsMap) =>
    filterCardsFromSpec(
      palworldFilterSpec,
      cards,
      filters,
      defaultFilters,
      deckCardsMap,
    ),
  sortDecklist,
  sortCards: (a: CardItem, b: CardItem) =>
    comparePalworldCardNumbers(
      (a as PalworldItem).card_number,
      (b as PalworldItem).card_number,
    ),

  // Card modal: Palworld shows its Pal type and work aptitudes as chips.
  getCardType: (card: CardItem) => (card as PalworldItem).card_kind ?? "",
  getAdditionalChips: (card: CardItem) => [
    ...((card as PalworldItem).type ?? []),
    ...((card as PalworldItem).aptitude ?? []),
  ],
  // The raw text is full of the official site's "@" symbol markup, so it goes
  // through the same badge renderer the card page uses. createElement rather
  // than JSX because this file is a .ts.
  getCardEffect: (card: CardItem) => [
    createElement(PalworldCardText, { text: (card as PalworldItem).text }),
  ],
  getCardTrigger: () => [],

  // Structures are printed sideways, so their art is stored landscape. Saying
  // so here is all any card-rendering component needs: none of them special
  // case Palworld.
  getCardOrientation: (card: CardItem) =>
    isPalworldStructure(card as PalworldItem) ? "landscape" : "portrait",

  getCost: (card: CardItem) => (card as PalworldItem).cost ?? 0,
  getEnergyCost: (card: CardItem) => (card as PalworldItem).cost ?? 0,
  getPower: (card: CardItem) => (card as PalworldItem).power ?? 0,
  getColor: (card: CardItem) => (card as PalworldItem).color,
};

export class PalworldHandler extends BaseHandler {
  constructor() {
    super(palworldConfig);
  }

  getFilterSpec() {
    return palworldFilterSpec;
  }

  getSet(card: CardItem): string {
    return (card as PalworldItem).set_id || "";
  }

  getTraits(card: CardItem): string[] {
    return (card as PalworldItem).type ?? [];
  }

  // Removed getSetImage override - using BaseHandler's .webp implementation
}
